import type React from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import {
    Card,
    Chip,
    Radio,
    RadioGroup,
    Typography,
    type TypographyProps,
} from '@heroui/react';
import { TextContext } from 'react-aria-components/Text';
import { countParam } from '@/pages/catalogImport/notices';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { RowDrawerBarcodeOption } from './rowDrawerResolutions';
import { RowDrawerTypedBarcode } from './RowDrawerTypedBarcode';
import type { RowDrawerBarcodeChoiceState as BarcodeChoice } from './useRowDrawer';

/** Inside the radio's label a `p` is not allowed: the text keeps Typography's look on a `span`. */
const asSpan: NonNullable<TypographyProps['render']> = ({
    children,
    ...domProps
}) => <span {...domProps}>{children}</span>;

const KEY = 'catalogImport.products.drawer.barcodeChoice';

/** How many of the case's codes the explanation lists before "and N more". */
const LISTED_VALUES = 3;

/** One option's title and what it saves, in the case's words. */
const optionText = (
    t: TFunction,
    choice: BarcodeChoice,
    option: RowDrawerBarcodeOption,
): { title: string; how: string[] } => {
    const count = choice.values.length;
    const rows = countParam(count);
    const kept = {
        count: choice.keepOthers + 1,
        main: choice.keepMain,
        others: countParam(choice.keepOthers),
    };
    switch (choice.case) {
        case 'imei':
            return {
                generate: {
                    title: t(`${KEY}.imei.generate`),
                    how: [t(`${KEY}.imei.generateHow`)],
                },
                keep: {
                    title: t(`${KEY}.imei.keep`),
                    how: [
                        t(`${KEY}.imei.keepHow`, kept),
                        t(`${KEY}.imei.keepNote`),
                    ],
                },
                type: {
                    title: t(`${KEY}.imei.type`),
                    how: [t(`${KEY}.imei.typeHow`)],
                },
            }[option];
        case 'scientific':
            return {
                generate: {
                    title: t(`${KEY}.scientific.generate`),
                    how: [t(`${KEY}.scientific.generateHow`)],
                },
                // Never offered: a scrambled value is not the old code.
                keep: { title: '', how: [] },
                type: {
                    title: t(`${KEY}.scientific.type`),
                    how: [t(`${KEY}.scientific.typeHow`)],
                },
            }[option];
        case 'notDigits':
            return {
                generate: {
                    title: t(`${KEY}.notDigits.generate`, { count }),
                    how: [t(`${KEY}.notDigits.generateHow`)],
                },
                keep: {
                    title: t(`${KEY}.notDigits.keep`, { count }),
                    how: [
                        t(`${KEY}.notDigits.keepHow`, kept),
                        t(`${KEY}.notDigits.keepNote`),
                    ],
                },
                type: {
                    title: t(`${KEY}.notDigits.type`),
                    how: [t(`${KEY}.notDigits.typeHow`, { count, rows })],
                },
            }[option];
    }
};

/** The case's title and explanation. */
const introText = (t: TFunction, choice: BarcodeChoice): string[] => {
    const count = choice.values.length;
    const rows = countParam(count);
    switch (choice.case) {
        case 'imei':
            return [
                t(`${KEY}.imei.title`, { count, rows }),
                t(`${KEY}.imei.about`),
            ];
        case 'scientific':
            return [t(`${KEY}.scientific.title`), t(`${KEY}.scientific.about`)];
        case 'notDigits':
            return [
                t(`${KEY}.notDigits.title`, { count }),
                t(`${KEY}.notDigits.about`, { count, rows }),
            ];
    }
};

type RowDrawerBarcodeChoiceProps = {
    choice: BarcodeChoice;
    onPick: (option: RowDrawerBarcodeOption) => void;
    onType: (value: string) => void;
    onUseAnyway: () => void;
};

/**
 * The barcode choice for the row's variant inside the problem card
 * (bos-app#232, boards `bc-DrawerImei`, `bc-DrawerScrambled`,
 * `bc-DrawerNotDigits`): the case's title and explanation with its codes
 * (the first three, "and N more"; a scrambled value as the cell holds it,
 * with how to avoid it), a restock's note, then "What should BOS save?" as
 * radio cards with what each saves, the suggested one marked: generate (with
 * the labels to print, one per unit), keep (not for a scrambled value) and
 * type, whose barcode field shows under the cards while it is picked. The
 * scope sentence names the variant's rows: Save writes the choice to all
 * of them.
 */
export const RowDrawerBarcodeChoice: React.FC<RowDrawerBarcodeChoiceProps> = ({
    choice,
    onPick,
    onType,
    onUseAnyway,
}) => {
    const { t } = useTranslation('pages');
    const [title, about] = introText(t, choice);
    const listed = choice.values.slice(0, LISTED_VALUES);
    const more = choice.values.length - listed.length;

    return (
        <div
            className="mt-2 flex w-full flex-col gap-3"
            {...getNodeTestIdProp(
                `catalog-import-row-drawer-barcode-choice-${choice.case}`,
            )}
        >
            <Typography type="body-sm" weight="medium">
                {title}
            </Typography>
            {choice.case === 'scientific' ? (
                <Typography type="body-sm">
                    {t(`${KEY}.scientific.cellHolds`)}{' '}
                    <span className="font-mono break-all">{listed[0]}</span>
                </Typography>
            ) : null}
            <Typography type="body-sm">{about}</Typography>
            {choice.case !== 'scientific' && listed.length > 0 ? (
                <Typography
                    type="body-sm"
                    {...getNodeTestIdProp(
                        'catalog-import-row-drawer-barcode-choice-values',
                    )}
                >
                    <span className="font-mono break-all">
                        {listed.join(', ')}
                    </span>
                    {more > 0
                        ? ` ${t(`${KEY}.more`, { more: countParam(more) })}`
                        : null}
                </Typography>
            ) : null}
            {choice.case === 'scientific' ? (
                <Typography color="muted" type="body-sm">
                    {t(`${KEY}.scientific.avoid`)}
                </Typography>
            ) : null}
            {choice.isRestock ? (
                <Typography type="body-sm">{t(`${KEY}.restock`)}</Typography>
            ) : null}
            <Typography type="body-sm" weight="medium">
                {t(`${KEY}.legend`)}
            </Typography>
            <RadioGroup
                aria-label={t(`${KEY}.legend`)}
                className="flex flex-col gap-2"
                onChange={(key) => {
                    const option = choice.options.find(
                        (candidate) => candidate === key,
                    );
                    if (option) {
                        onPick(option);
                    }
                }}
                value={choice.picked}
            >
                {choice.options.map((option) => {
                    const { title: optionTitle, how } = optionText(
                        t,
                        choice,
                        option,
                    );
                    return (
                        <Card
                            className="p-0"
                            key={option}
                            variant="secondary"
                            {...getNodeTestIdProp(
                                `catalog-import-row-drawer-barcode-option-${option}`,
                            )}
                        >
                            <Radio className="mt-0 w-full" value={option}>
                                <Radio.Content className="w-full items-start gap-3 p-3">
                                    <Radio.Control className="mt-0.5">
                                        <Radio.Indicator />
                                    </Radio.Control>
                                    {/* The radio offers its text only a
                                        description slot; these lines are
                                        part of its label. */}
                                    <TextContext.Provider value={null}>
                                        <span className="flex min-w-0 flex-1 flex-col gap-1">
                                            <span className="flex flex-wrap items-center gap-2">
                                                <Typography
                                                    render={asSpan}
                                                    type="body-sm"
                                                    weight="medium"
                                                >
                                                    {optionTitle}
                                                </Typography>
                                                {choice.suggested === option ? (
                                                    <Chip
                                                        color="accent"
                                                        size="sm"
                                                        variant="soft"
                                                    >
                                                        {t(`${KEY}.suggested`)}
                                                    </Chip>
                                                ) : null}
                                            </span>
                                            {how.map((line) => (
                                                <Typography
                                                    color="muted"
                                                    key={line}
                                                    render={asSpan}
                                                    type="body-sm"
                                                >
                                                    {line}
                                                </Typography>
                                            ))}
                                            {option === 'generate' ? (
                                                <Typography
                                                    render={asSpan}
                                                    type="body-sm"
                                                    weight="medium"
                                                    {...getNodeTestIdProp(
                                                        'catalog-import-row-drawer-barcode-labels',
                                                    )}
                                                >
                                                    {choice.labels > 0
                                                        ? t(
                                                              `${KEY}.labelsPerUnit`,
                                                              {
                                                                  count: choice.labels,
                                                                  labels: countParam(
                                                                      choice.labels,
                                                                  ),
                                                              },
                                                          )
                                                        : t(`${KEY}.noLabels`)}
                                                </Typography>
                                            ) : null}
                                        </span>
                                    </TextContext.Provider>
                                </Radio.Content>
                            </Radio>
                        </Card>
                    );
                })}
            </RadioGroup>
            {choice.case === 'scientific' ? (
                <Typography color="muted" type="body-xs">
                    {t(`${KEY}.scientific.noKeep`)}
                </Typography>
            ) : null}
            {choice.picked === 'type' ? (
                <RowDrawerTypedBarcode
                    onChange={onType}
                    onUseAnyway={onUseAnyway}
                    status={choice.typedStatus}
                    value={choice.typed}
                />
            ) : null}
            <Typography
                color="muted"
                type="body-xs"
                {...getNodeTestIdProp(
                    'catalog-import-row-drawer-barcode-choice-scope',
                )}
            >
                {t(`${KEY}.scope`, {
                    count: choice.rows.length,
                    rows: countParam(choice.rows.length),
                    sku: choice.variantLabel,
                })}
            </Typography>
        </div>
    );
};
